<script lang="ts">
  import LoadingSpinner from "./LoadingSpinner.svelte";

  import { api } from "../api/client";
  import { track } from "../helpers/mixpanel";

  export let buyerId: string;

  // true while we're dispatching the patch req, to show a spinner and block form edits.
  let submitting = false;

  // state refreshed by retrieval from backend
  let hasTwo = false;
  let errors: string[] = [];

  // refreshed by retrieval from backend, modified via form, on successful form submission updated
  // with whatever new values were persisted (NOT updated on failed form submission so people don't
  // lose whatever they entered).
  let toAddresses: string[] = [];
  let ccAddresses: string[] = [];

  // just used for button copy, shouldn't change with form data. refreshed when we get the form
  // submission response.
  let allAddresses: string[] = [];

  function serialize() {
    return {
      to_addresses: toAddresses,
      cc_addresses: ccAddresses,
    };
  }

  function deserialize(s: string) {
    const x = JSON.parse(s);

    const newAll = new Array<string>();

    const addToAll = (function () {
      const seen = new Set<string>();
      return (xs: string[]) => {
        for (const s of xs) {
          if (!seen.has(s)) {
            newAll.push(s);
            seen.add(s);
          }
        }
      };
    })();

    // poor man's validation
    const isStringArray = (xs: any): xs is string[] =>
      Array.isArray(xs) && xs.every((x) => typeof x === "string");
    let y: any;
    if ("to_addresses" in x && (y = x["to_addresses"]) && isStringArray(y)) {
      addToAll((toAddresses = y));
    }

    if ("cc_addresses" in x && (y = x["cc_addresses"]) && isStringArray(y)) {
      addToAll((ccAddresses = y));
    }

    allAddresses = newAll;
  }

  async function lookupStatementEmails(buyerID: string) {
    const resp = await api.get(`two/${buyerID}/statement-email-addresses`);

    if (resp.status === 200) {
      deserialize(await resp.text());
      hasTwo = true;
      return;
    }

    if (resp.status !== 204) {
      // 204 means no content / no two info
      console.error("statement email lookup got some unexpected status", resp);
    }

    hasTwo = false;
  }

  async function save(_e: Event, buyerID: string) {
    submitting = true;
    errors = [];

    const resp = await (async function () {
      try {
        return await api.patch(`two/${buyerID}/statement-email-addresses`, {
          json: serialize(),
          throwHttpErrors: false,
        });
      } finally {
        submitting = false;
      }
    })();

    if (resp.status === 200) {
      deserialize(await resp.text());
      hasTwo = true;
      dialog.close();

      return;
    } else {
      if (resp.status === 400) {
        const submissionErrors = await resp.json<{ errors: string[] }>();

        if (submissionErrors.errors && submissionErrors.errors.length) {
          errors = submissionErrors.errors;
          return;
        }
      }

      errors = ["Failed to save. Please try again, and contact support if this continues."];
    }
  }

  function cancel(_e: Event) {
    // don't actually cancel any in-flight request, in case it succceeds we should still update our
    // state with whatever the new values are.
    dialog.close();
  }

  let dialog: HTMLDialogElement;

  // old safari </3
  const hasDialog = typeof HTMLDialogElement !== "undefined";

  function entryButtonClick(_e: Event) {
    if (hasDialog) {
      dialog.showModal();
      track("settings_statement_emails_click");
    } else {
      alert(
        "Please update to a newer browser version to update statement email addresses, or " +
          "contact support."
      );
    }
  }

  // easy testing (uncomment so you don't have to re-click to open on every pageload).
  //  $: if (hasTwo && dialog) dialog.showModal();
</script>

{#await lookupStatementEmails(buyerId) then ignored}
  {#if hasTwo}
    <button class="entry" on:click={entryButtonClick}>statement emails</button>
    <!-- needs the keypress to make svelte think we're not breaking accessibility (the click handler
  is only handling clicking outside to close the dialog, and the UA already handles Esc for close)
-->
    {#if hasDialog}
      <dialog bind:this={dialog}>
        <form method="dialog" on:submit|preventDefault={(e) => save(e, buyerId)}>
          <h1>Statement Email Addresses</h1>
          <p>where your credit provider sends statements &amp; payment reminders</p>
          <fieldset disabled={submitting}>
            <legend>To Addresses</legend>
            <ul>
              {#each toAddresses as addr, i}
                <li class="address-row">
                  <input
                    type="email"
                    bind:value={addr}
                    placeholder="accounting@restaurant.com"
                    required
                  />
                  <!-- don't let people delete them all, we would like people to pay.-->
                  <button
                    disabled={toAddresses.length === 1}
                    type="button"
                    on:click={() => {
                      toAddresses.splice(i, 1);
                      toAddresses = toAddresses;
                    }}>delete</button
                  >
                </li>
              {/each}
              <button
                type="button"
                style="margin-top: 10px"
                on:click={() => {
                  toAddresses = toAddresses.concat("");
                }}>add {toAddresses.length ? "another" : "one"}</button
              >
            </ul>
          </fieldset>

          <fieldset disabled={submitting}>
            <legend>CC Addresses</legend>
            <ul>
              {#each ccAddresses as addr, i}
                <li class="address-row">
                  <input
                    type="email"
                    bind:value={addr}
                    placeholder="accounting@restaurant.com"
                    required
                  />
                  <button
                    type="button"
                    on:click={() => {
                      ccAddresses.splice(i, 1);
                      ccAddresses = ccAddresses;
                    }}>delete</button
                  >
                </li>
              {/each}
              <button
                type="button"
                on:click={() => {
                  ccAddresses = ccAddresses.concat("");
                }}>add {ccAddresses.length ? "another" : "one"}</button
              >
            </ul>
          </fieldset>

          {#each errors as error}
            <p class="errors">{error}</p>
          {/each}

          <div class="buttons">
            <button type="submit" disabled={submitting}>
              {#if submitting}
                <LoadingSpinner />
              {:else}
                save
              {/if}
            </button>
            <button class="secondary" on:click|preventDefault={cancel}>cancel</button>
          </div>
        </form>
      </dialog>
    {/if}
  {/if}
{/await}

<style>
  button.entry {
    font-family: "REKKI-Regular";
    background: none;
    color: var(--rekki-neutral-04);
    font-size: 16px;
    line-height: 48px;
    padding: 4px 12px;
    border: none;
    width: 100%;
    text-align: left;
    border-radius: 8px;
  }

  button.entry:hover {
    color: var(--rekki-dark);
    background: var(--rekki-neutral-01);
  }

  dialog {
    background-color: var(--rekki-neutral-01);
    border-radius: 8px;
    max-height: 80%;
    padding: 35px;
    margin: auto;
  }

  dialog::backdrop {
    background-color: rgba(0, 0, 0, 0.6);
  }

  dialog * {
    font-family: "REKKI-OCD";
    text-transform: uppercase;
  }

  /* all of the following rules are to try to restore some whitespace after we applied a css reset
   * to set margin and padding to 0 everywhere. none of these values are particularly special, they
   * just approximate the pre-reset version and make this thing look less like tightly packed shit.
   */
  h1 {
    margin-top: 0.67em;
    margin-bottom: 0.67em;
  }

  p {
    margin-top: 1em;
    margin-bottom: 1em;
  }

  fieldset {
    margin: 15px 2px 0px 2px;
    padding: 5px 10px 9px 10px;
  }

  ul {
    margin: 14px 0px;
    padding-left: 40px;
  }

  /* end of post-css reset stuff. */

  input[type="email"] {
    font-family: REKKI-Regular;
    text-transform: revert;
  }

  ul {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .address-row {
    display: flex;
    align-items: center;
    column-gap: 10px;
  }

  .buttons {
    display: flex;
    flex-direction: row-reverse;
    column-gap: 10px;
    margin-top: 10px;
  }

  dialog button {
    display: inline-block;
    box-sizing: border-box;
    border-radius: 40px;
    /*font-family: "REKKI-Regular";*/
    padding: 12px 12px;
    border: 0 none;
    white-space: nowrap;
    /* normal button: dark */
    background-color: var(--rekki-dark);
    color: var(--rekki-light);
  }

  dialog button[disabled] {
    background-color: var(--rekki-neutral-02);
    color: var(--rekki-transparency-dark);
  }

  dialog button.secondary {
    /* neutral-02 */
    background-color: var(--rekki-neutral-02);
    color: var(--rekki-dark);
  }

  p.errors {
    /* trying to keep this from growing the popup horizontally, it makes everything really annoying.
      https://stackoverflow.com/a/73654975 */
    min-width: 100%;
    max-width: min-content;
  }

  /* leaving it here since i did the work to piece out the relevant colors */
  /*
  dialog button.secondary[disabled] {
    /* neutral-02 */
  /*
    background-color: var(--rekki-neutral-01);
    color: var(--rekki-dark);
  }
  */

  dialog button:active {
    filter: brightness(1.1);
  }
</style>
